import React, { createContext, useContext, useState, useCallback } from 'react';

export interface NavbarContextType {
  title: string;
  setTitle: (title: string) => void;
  showBackButton: boolean;
  onBackClick?: () => void;
  setNavbarMode: (showBackButton: boolean, onBackClick?: () => void) => void;
  resetNavbarMode: () => void;
}

const NavbarContext = createContext<NavbarContextType | undefined>(undefined);

/**
 * NavbarProvider - Provides dynamic navbar state management
 *
 * Wraps the app to allow any child component to:
 * - Set the navbar title dynamically (for detail pages)
 * - Control navbar mode: hamburger menu vs back button (for navigation)
 *
 * Supports titles from various sources: athletes, artworks, or any other data
 * Supports mode switching for detail pages that need back button instead of menu
 *
 * Usage:
 * <NavbarProvider>
 *   <App />
 * </NavbarProvider>
 */
export function NavbarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [title, setTitle] = useState('');
  const [showBackButton, setShowBackButton] = useState(false);
  const [onBackClick, setOnBackClick] = useState<(() => void) | undefined>(
    undefined
  );

  const setNavbarMode = useCallback((showBack: boolean, callback?: () => void) => {
    setShowBackButton(showBack);
    setOnBackClick(() => callback);
  }, []);

  const resetNavbarMode = useCallback(() => {
    setShowBackButton(false);
    setOnBackClick(undefined);
  }, []);

  return (
    <NavbarContext.Provider
      value={{
        title,
        setTitle,
        showBackButton,
        onBackClick,
        setNavbarMode,
        resetNavbarMode,
      }}
    >
      {children}
    </NavbarContext.Provider>
  );
}

/**
 * Hook to access navbar context
 * Use this in any component to control navbar state (title and mode)
 *
 * Example - Set dynamic title:
 * const { setTitle } = useNavbar();
 * useEffect(() => {
 *   if (athlete) {
 *     setTitle(`${athlete.firstName} ${athlete.lastName}`);
 *   }
 * }, [athlete, setTitle]);
 *
 * Example - Show back button instead of menu:
 * const { setNavbarMode, resetNavbarMode } = useNavbar();
 * const navigate = useNavigate();
 * useEffect(() => {
 *   setNavbarMode(true, () => navigate(-1));
 *   return () => resetNavbarMode(); // Reset on unmount
 * }, [navigate, setNavbarMode, resetNavbarMode]);
 */
export function useNavbar(): NavbarContextType {
  const context = useContext(NavbarContext);
  if (context === undefined) {
    throw new Error('useNavbar must be used within NavbarProvider');
  }
  return context;
}

// Legacy alias for backward compatibility during migration
export const useNavbarTitle = useNavbar;

export default NavbarContext;
